/**
 * Client-side photo processing before upload. Every picked file is decoded
 * and re-encoded down to a "display" version plus a small JPEG thumbnail:
 *
 * - Decode via createImageBitmap — applies EXIF orientation, and is the
 *   gate for format support. HEIC decodes only in Safari (which then ships
 *   a JPEG); browsers that can't decode a file couldn't display it either,
 *   so the file is REJECTED with UnsupportedImageError rather than uploaded.
 * - Display: long edge capped at 2560px, re-encoded JPEG, then compressed to
 *   fit a byte budget — quality backs off, then dimensions, until it's under
 *   DISPLAY_TARGET_BYTES. PNG is kept (transparency) only if a full-size encode
 *   already fits; an oversized PNG is lossless and can't be quality-reduced, so
 *   it falls back to a compress-to-fit JPEG. Re-encoding strips ALL metadata —
 *   including GPS EXIF, which photos of customers' homes should never carry
 *   into storage.
 * - Animated GIFs pass through untouched (re-encoding would freeze them);
 *   their thumb still comes from the decoded first frame. One that exceeds the
 *   upload cap is rejected up front (it can't be compressed).
 *
 * The server enforces the byte caps independently (signed Content-Length +
 * confirm-time stat) — this module is for size/quality, not security.
 */

import { fillOpaqueBackdropForLossy } from "@salestrak/ui";
import { maxBytesForContentType, MAX_IMAGE_BYTES } from "#app/lib/media-kind";

const DISPLAY_MAX_EDGE = 2560;
const DISPLAY_QUALITY = 0.82;
// Compress-to-fit budget for the display image. Well above a normal 2560px
// JPEG (~0.5 MB), so typical photos encode in one pass with no extra
// compression; only outliers (huge PNGs, ultra-detailed images) get squeezed.
const DISPLAY_TARGET_BYTES = 2 * 1024 * 1024;
// Backoff floors for compress-to-fit: step quality down to MIN_QUALITY first,
// then shrink the long edge by EDGE_BACKOFF_FACTOR down to MIN_DISPLAY_EDGE.
const MIN_QUALITY = 0.5;
const QUALITY_STEP = 0.12;
const EDGE_BACKOFF_FACTOR = 0.8;
const MIN_DISPLAY_EDGE = 1280;
const THUMB_MAX_EDGE = 256;
const THUMB_QUALITY = 0.8;

// HEIC/HEIF is the common culprit: it's the default iPhone camera format and
// only Safari/WebKit can decode it, so on Chrome/Firefox/Android the picker
// rejects it. Detect it (by MIME or extension) to give an actionable message
// instead of a bare "unsupported format".
function isHeic(file: File): boolean {
  return /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

export class UnsupportedImageError extends Error {
  constructor(file: File) {
    const base = `"${file.name}" isn't a supported image format`;
    super(
      isHeic(file)
        ? `${base}. iPhone HEIC photos only work in Safari — set your iPhone's camera to "Most Compatible" (Settings ▸ Camera ▸ Formats) or save the photo as JPEG first.`
        : base,
    );
    this.name = "UnsupportedImageError";
  }
}

/** A file over its media class's upload cap. Images are normally compressed to
 * fit, so this only fires for the bytes we can't shrink: animated GIFs and the
 * non-image kinds (video/pdf/doc), which upload as-is. Caught up front so the
 * user gets a clear, immediate message instead of a full upload that the server
 * rejects at presign. */
export class FileTooLargeError extends Error {
  constructor(file: File, capBytes: number) {
    const mb = Math.round(capBytes / (1024 * 1024));
    super(`"${file.name}" is too large to upload (over ${mb} MB).`);
    this.name = "FileTooLargeError";
  }
}

export interface ProcessedMedia {
  /** Display bytes to upload (re-encoded unless passed through). */
  blob: Blob;
  contentType: string;
  /** Small JPEG thumbnail, or null when one couldn't be produced. */
  thumb: Blob | null;
}

function scaledDimensions(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function encode(
  bitmap: ImageBitmap,
  maxEdge: number,
  type: string,
  quality: number,
): Promise<Blob | null> {
  const { width, height } = scaledDimensions(bitmap.width, bitmap.height, maxEdge);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  fillOpaqueBackdropForLossy(ctx, width, height, type);
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Re-encodes to JPEG, backing off quality (down to MIN_QUALITY) then dimensions
 * (down to MIN_DISPLAY_EDGE) until the blob fits `targetBytes`. If even the
 * smallest attempt is over, returns that best effort — a floor-quality,
 * floor-size JPEG is comfortably under the server cap. The decoded bitmap is
 * reused across attempts, so each retry is just another canvas.toBlob; a normal
 * photo fits on the first pass, so there's no extra work in the common case.
 */
async function encodeToFitJpeg(
  bitmap: ImageBitmap,
  maxEdge: number,
  quality: number,
  targetBytes: number,
): Promise<Blob | null> {
  let edge = maxEdge;
  let best: Blob | null = null;
  while (true) {
    for (let q = quality; q >= MIN_QUALITY - 1e-9; q -= QUALITY_STEP) {
      const blob = await encode(bitmap, edge, "image/jpeg", q);
      if (!blob) return best;
      best = blob;
      if (blob.size <= targetBytes) return blob;
    }
    const nextEdge = Math.round(edge * EDGE_BACKOFF_FACTOR);
    if (nextEdge < MIN_DISPLAY_EDGE) return best;
    edge = nextEdge;
  }
}

/**
 * The format-support gate on its own, without the resize/re-encode work.
 * `createImageBitmap` is the single source of truth for "can the browser decode
 * this" — callers that do their own encoding (the avatar cropper, which decodes
 * a picked file via an HTMLImageElement that fails SILENTLY on HEIC) call this
 * first so an unsupported file is rejected up front with the same actionable
 * UnsupportedImageError instead of a blank, dead-end UI. The returned bitmap is
 * the caller's to `.close()`.
 */
export async function decodeImageOrThrow(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    throw new UnsupportedImageError(file);
  }
}

/**
 * Capture a video's poster frame as a small JPEG via a hidden <video> + canvas
 * (no extra dependency). Best-effort: resolves null on any decode/seek failure
 * or after a timeout, so the attachment falls back to a file card. The thumb
 * feeds the existing thumb slot, so the note tile shows the poster.
 */
function captureVideoPosterThumb(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;
    const finish = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(blob);
    };
    // Safety net: never hang the upload on a video that won't decode/seek.
    const timer = setTimeout(() => finish(null), 5000);
    const done = (blob: Blob | null) => {
      clearTimeout(timer);
      finish(blob);
    };
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.onloadeddata = () => {
      // Seek ~10% in to skip a black/fade-in intro and land on a representative
      // frame, clamped to [0.1s, 1s]: at least off frame 0, but never deep into
      // a long clip. Unknown/zero duration falls back to 0.1s.
      try {
        const d = video.duration;
        video.currentTime =
          Number.isFinite(d) && d > 0 ? Math.min(Math.max(d * 0.1, 0.1), 1) : 0.1;
      } catch {
        // Some browsers seek lazily — onseeked may still fire from load.
      }
    };
    video.onseeked = () => {
      try {
        const { width, height } = scaledDimensions(
          video.videoWidth,
          video.videoHeight,
          THUMB_MAX_EDGE,
        );
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return done(null);
        ctx.drawImage(video, 0, 0, width, height);
        canvas.toBlob((b) => done(b), "image/jpeg", THUMB_QUALITY);
      } catch {
        done(null);
      }
    };
    video.onerror = () => done(null);
    video.src = url;
  });
}

export async function processMediaForUpload(file: File): Promise<ProcessedMedia> {
  // Non-image classes skip image decode/re-encode entirely — the bytes upload
  // as-is, so their size can't be compressed down. Pre-check against the
  // per-kind server cap (video 200MB / doc 25MB) up front: without this the
  // file is fully processed and the PUT attempted before the server rejects it
  // at presign with a generic error. (Images fall through to the compress-to-fit
  // path below, where size is handled by re-encoding.)
  if (!file.type.startsWith("image/")) {
    const cap = maxBytesForContentType(file.type);
    if (file.size > cap) throw new FileTooLargeError(file, cap);
  }

  // PDFs and videos get a best-effort JPEG thumbnail (rendered page 1 / poster
  // frame); office docs have no client-renderable preview (null thumb → file
  // card). Any thumb failure degrades to null, never blocks the upload.
  if (file.type === "application/pdf") {
    const thumb = await import("./pdf-thumb")
      .then((m) => m.renderPdfPosterThumb(file))
      .catch((err) => {
        // Non-fatal: the PDF still uploads and falls back to a file card.
        // Logged so a worker/render failure is diagnosable, not silent.
        console.warn("[media] PDF thumbnail failed", err);
        return null;
      });
    return { blob: file, contentType: "application/pdf", thumb };
  }
  if (file.type.startsWith("video/")) {
    const thumb = await captureVideoPosterThumb(file).catch(() => null);
    return { blob: file, contentType: file.type, thumb };
  }
  if (!file.type.startsWith("image/")) {
    // Office docs (docx/xlsx/pptx/csv/txt, …) — pass through, no preview.
    return { blob: file, contentType: file.type, thumb: null };
  }

  const bitmap = await decodeImageOrThrow(file);

  try {
    if (file.type === "image/gif") {
      // Pass-through preserves animation (canvas only holds the first frame),
      // so a GIF can't be compressed — reject one that won't clear the cap
      // rather than letting its PUT fail with a generic error.
      if (file.size > MAX_IMAGE_BYTES) throw new FileTooLargeError(file, MAX_IMAGE_BYTES);
      const thumb = await encode(bitmap, THUMB_MAX_EDGE, "image/jpeg", THUMB_QUALITY);
      return { blob: file, contentType: "image/gif", thumb };
    }

    // Thumb is always a tiny JPEG — kick it off so it overlaps the display work.
    const thumbPromise = encode(bitmap, THUMB_MAX_EDGE, "image/jpeg", THUMB_QUALITY);

    let display: Blob | null;
    let contentType: string;
    if (file.type === "image/png") {
      // Keep PNG (transparency) only if a full-size encode already fits the
      // budget. PNG is lossless — an oversized one can't be quality-reduced, so
      // fall back to a compress-to-fit JPEG (roofing photos don't need
      // transparency, and the size win is large).
      const png = await encode(bitmap, DISPLAY_MAX_EDGE, "image/png", 1);
      if (png && png.size <= DISPLAY_TARGET_BYTES) {
        display = png;
        contentType = "image/png";
      } else {
        display = await encodeToFitJpeg(
          bitmap,
          DISPLAY_MAX_EDGE,
          DISPLAY_QUALITY,
          DISPLAY_TARGET_BYTES,
        );
        contentType = "image/jpeg";
      }
    } else {
      // JPEG (and HEIC sources Safari decoded, webp, …) → compress-to-fit JPEG.
      display = await encodeToFitJpeg(
        bitmap,
        DISPLAY_MAX_EDGE,
        DISPLAY_QUALITY,
        DISPLAY_TARGET_BYTES,
      );
      contentType = "image/jpeg";
    }

    const thumb = await thumbPromise;
    if (!display) throw new UnsupportedImageError(file);
    return { blob: display, contentType: display.type || contentType, thumb };
  } finally {
    bitmap.close();
  }
}
